// ═══════════════════════════════════════════════════════════════════
// signatureInk v242.64.0 (BUG-5)
//
// ONE definition of "this canvas actually has a signature on it".
//
// Why this file exists. The app had THREE signature captures and three
// different answers to that question:
//
//   ShareScreen.saveSignature()        toDataURL() unconditionally  -> a BLANK
//                                      canvas is a perfectly valid PNG, so
//                                      tapping "Sign & Accept" without drawing
//                                      recorded agreementSigned = true.
//   MDCaseOnboarding.stopDrawing()     writes the dataURL whenever isDrawing is
//                                      true - which a bare TAP sets, because
//                                      startDrawing only does beginPath/moveTo.
//                                      One tap, no ink, signed. (That screen was
//                                      never routed; deleted in v242.239.0, R36 /
//                                      PROD-211.)
//   SignAgreementScreen                hasInk, set only inside moveDraw, gating
//                                      a disabled button. Correct.
//
// Per ORELLA_RULES.md 1h the fix is not to make three copies agree - a comment
// saying they agree is a promise, calling the same function is a property - and
// per the same rule we unify on the definition that ALREADY HAS CONSEQUENCES,
// which is SignAgreementScreen's.
//
// This is deliberately a PIXEL test, not an event-bookkeeping flag. A flag
// records what we think happened; the pixels record what is actually on the
// canvas, and they cannot be fooled by a stray touchstart/touchend pair.
// ═══════════════════════════════════════════════════════════════════

/**
 * True if any pixel on the canvas is non-transparent.
 * Returns false for a null/zero-sized canvas, and false if the pixel data
 * cannot be read - fail CLOSED here on purpose: this gates a consent record,
 * and refusing a real signature is recoverable (draw again) while accepting a
 * blank one is not (ORELLA_RULES.md 4 - fail closed when the cost is theirs).
 */
export function canvasHasInk(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return false
  try {
    const ctx = canvas.getContext('2d')
    if (!ctx) return false
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
    // RGBA; alpha is every 4th byte. Any non-zero alpha means something was drawn.
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 0) return true
    }
    return false
  } catch (e) {
    console.warn('[signatureInk] could not read canvas pixels:', e && e.message)
    return false
  }
}

/**
 * The signature data URL, or null when the canvas is blank.
 * Every save path should use THIS rather than calling toDataURL directly -
 * that call is the defect, because it always succeeds.
 */
export function signatureDataUrl(canvas) {
  if (!canvasHasInk(canvas)) return null
  return canvas.toDataURL('image/png')
}

// ─── self-tests, run with: node src/utils/signatureInk.js (needs a canvas shim) ───
// Kept as documentation of the cases that matter:
//   blank canvas            -> canvasHasInk false, signatureDataUrl null
//   single tap (moveTo only)-> canvasHasInk false  <- the defect
//   one short stroke        -> canvasHasInk true
//   cleared after a stroke  -> canvasHasInk false
